import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { urlSeguraONull } from '../utils/urlSegura';
import { descargarICS } from '../utils/ics';
import { useConfigSitio } from '../hooks/useConfigSitio';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
L.Marker.prototype.options.icon = L.icon({ iconUrl: markerIcon, shadowUrl: markerShadow, iconSize: [25, 41], iconAnchor: [12, 41] });

// Pin con emoji y color, para distinguir bien vs. lugar del remate en el mapa combinado.
function pinEmoji(emoji, color) {
  return L.divIcon({
    html: `<div style="background:${color};width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.4);border:2px solid white;"><span style="transform:rotate(45deg);font-size:15px;line-height:1;">${emoji}</span></div>`,
    className: '',
    iconSize: [34, 34],
    iconAnchor: [17, 34],
    popupAnchor: [0, -32],
  });
}
const ICONO_BIEN = pinEmoji('🏠', '#2563eb');
const ICONO_REMATE = pinEmoji('🔨', '#dc2626');

// Distancia en km entre dos puntos [lat, lng] (haversine).
function distanciaKm([lat1, lng1], [lat2, lng2]) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180);
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Ajusta el encuadre del mapa para que entren todos los puntos.
function AjustarEncuadre({ puntos }) {
  const map = useMap();
  useEffect(() => {
    if (puntos.length === 1) map.setView(puntos[0], 15);
    else if (puntos.length > 1) map.fitBounds(puntos, { padding: [45, 45], maxZoom: 16 });
  }, [map, puntos]);
  return null;
}

const UMBRAL_MAPA_UNICO_KM = 3;

const ESTADO_LABEL = { proximo: 'Próximo', realizado: 'Realizado', suspendido: 'Suspendido' };
const ESTADO_COLOR = { proximo: '#1d4ed8', realizado: '#15803d', suspendido: '#b91c1c' };
const MODALIDAD_LABEL = { presencial: '📍 Presencial', online: '💻 Online', mixto: '📍💻 Presencial y online' };

function aFecha(valor) {
  if (!valor) return null;
  const d = valor.toDate ? valor.toDate() : new Date(valor);
  return isNaN(d.getTime()) ? null : d;
}
function fmtFechaHora(valor) {
  const d = aFecha(valor);
  return d ? d.toLocaleString('es-AR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
}
function fmtFecha(valor) {
  const d = aFecha(valor);
  return d ? d.toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
}

export default function RemateDetalle() {
  // El parámetro puede ser el slug (URL amigable) o el id (links viejos y de los emails).
  const { id: param } = useParams();
  const navigate = useNavigate();
  const config = useConfigSitio();
  const [idReal, setIdReal] = useState(null);   // doc id real, independiente de la URL
  const [r, setR] = useState(null);
  const [vendedor, setVendedor] = useState(null);
  const [noEncontrado, setNoEncontrado] = useState(false);
  const [error, setError] = useState(null);
  const [fotoActiva, setFotoActiva] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    async function cargar() {
      try {
        // Primero por slug; si no aparece, se asume que es un id (link viejo).
        let data = null;
        let docId = null;
        const sSnap = await getDocs(query(collection(db, 'remates'), where('slug', '==', param)));
        if (!sSnap.empty) {
          docId = sSnap.docs[0].id;
          data = sSnap.docs[0].data();
        } else {
          const snap = await getDoc(doc(db, 'remates', param));
          if (!snap.exists()) { setNoEncontrado(true); return; }
          docId = snap.id;
          data = snap.data();
          // Link viejo por id: llevamos a la URL amigable si el remate ya tiene slug.
          if (data.slug && !data.eliminado) { navigate(`/remate/${data.slug}`, { replace: true }); return; }
        }
        if (data.eliminado) { setNoEncontrado(true); return; }
        setIdReal(docId);
        setR(data);
        if (data.martilleroId) {
          const vSnap = await getDoc(doc(db, 'vendedores', data.martilleroId));
          if (vSnap.exists()) setVendedor({ id: data.martilleroId, ...vSnap.data() });
        }
      } catch (err) {
        console.error(err);
        setError('No se pudo cargar el remate. Intentá de nuevo.');
      }
    }
    cargar();
  }, [param, navigate]);

  const fotos = r ? ((r.fotosUrls?.length ? r.fotosUrls : [r.fotoUrl]).filter(Boolean)) : [];
  const fotosUsables = fotos.length ? fotos : ['/sin-imagen.svg'];
  const prevFoto = useCallback((e) => { e?.stopPropagation(); setFotoActiva(i => (i - 1 + fotosUsables.length) % fotosUsables.length); }, [fotosUsables.length]);
  const nextFoto = useCallback((e) => { e?.stopPropagation(); setFotoActiva(i => (i + 1) % fotosUsables.length); }, [fotosUsables.length]);

  if (error) return (
    <div style={{ padding: '50px', textAlign: 'center' }}>
      <p style={{ color: '#dc3545', fontSize: '16px' }}>{error}</p>
      <Link to="/remates" style={{ color: '#007bff' }}>← Volver a remates</Link>
    </div>
  );
  if (noEncontrado) return (
    <div style={{ padding: '50px', textAlign: 'center' }}>
      <h2 style={{ color: '#333' }}>Remate no encontrado</h2>
      <p style={{ color: '#888' }}>Es posible que haya sido eliminado o el enlace sea incorrecto.</p>
      <Link to="/remates" style={{ color: '#007bff', fontWeight: 'bold' }}>← Volver a remates</Link>
    </div>
  );
  if (!r) return <div style={{ padding: '50px', textAlign: 'center' }}>Cargando ficha del remate…</div>;

  const inicio = aFecha(r.fechaHora);
  const agendar = () => { if (inicio) descargarICS({ ...r, id: idReal }, inicio, window.location.href); };
  const moneda = r.moneda === 'USD' ? 'U$S' : '$';

  return (
    <div className="remate-wrap" style={{ padding: '40px', maxWidth: '1100px', margin: '0 auto', boxSizing: 'border-box' }}>
      <style>{`
        @media (max-width: 820px) {
          .remate-grid { grid-template-columns: 1fr !important; }
          .remate-wrap { padding: 20px !important; }
        }
      `}</style>

      {lightbox && (
        <div onClick={() => setLightbox(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.93)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button onClick={() => setLightbox(false)} style={{ position: 'absolute', top: '18px', right: '22px', background: 'rgba(255,255,255,0.12)', border: 'none', color: 'white', fontSize: '26px', cursor: 'pointer', borderRadius: '50%', width: '44px', height: '44px' }}>✕</button>
          {fotosUsables.length > 1 && <button onClick={prevFoto} style={{ position: 'absolute', left: '16px', background: 'rgba(255,255,255,0.12)', border: 'none', color: 'white', fontSize: '32px', cursor: 'pointer', borderRadius: '50%', width: '52px', height: '52px' }}>‹</button>}
          <img src={fotosUsables[fotoActiva]} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '10px' }} />
          {fotosUsables.length > 1 && <button onClick={nextFoto} style={{ position: 'absolute', right: '16px', background: 'rgba(255,255,255,0.12)', border: 'none', color: 'white', fontSize: '32px', cursor: 'pointer', borderRadius: '50%', width: '52px', height: '52px' }}>›</button>}
        </div>
      )}

      <Link to="/remates" style={{ color: '#007bff', textDecoration: 'none', fontWeight: 'bold' }}>← Volver a remates</Link>

      <div className="remate-grid" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '40px', marginTop: '20px' }}>

        {/* IZQUIERDA: galería + bien */}
        <div style={{ minWidth: 0 }}>
          <div style={{ background: 'white', padding: '15px', borderRadius: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
            <div style={{ position: 'relative', cursor: 'zoom-in', background: '#f0f0f0', borderRadius: '10px', overflow: 'hidden', height: '420px' }} onClick={() => setLightbox(true)}>
              <img src={fotosUsables[fotoActiva]} onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = '/sin-imagen.svg'; }} alt="Remate" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
              {fotosUsables.length > 1 && (
                <span style={{ position: 'absolute', bottom: '10px', left: '12px', background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: '11px', padding: '4px 10px', borderRadius: '20px' }}>
                  {fotoActiva + 1} / {fotosUsables.length}
                </span>
              )}
            </div>
            {fotosUsables.length > 1 && (
              <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', marginTop: '12px' }}>
                {fotosUsables.map((url, idx) => (
                  <img key={idx} src={r.fotosThumbs?.[idx] || url} alt={`foto ${idx + 1}`} loading="lazy"
                    onClick={() => setFotoActiva(idx)}
                    style={{ width: '88px', height: '66px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0, cursor: 'pointer', border: fotoActiva === idx ? '3px solid #007bff' : '2px solid transparent', opacity: fotoActiva === idx ? 1 : 0.75 }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Descripción del bien */}
          <div style={{ background: 'white', padding: '30px', borderRadius: '15px', marginTop: '20px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
            <h3 style={{ marginTop: 0 }}>Descripción del bien</h3>
            <p style={{ color: '#444', lineHeight: '1.8', whiteSpace: 'pre-wrap', margin: 0 }}>{r.descripcion}</p>
            {r.ocupado && (
              <p style={{ margin: '16px 0 0', padding: '10px 14px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', color: '#92400e', fontSize: '14px', fontWeight: '600' }}>
                ⚠️ El bien se encuentra ocupado.
              </p>
            )}
          </div>

          {/* Causa judicial */}
          {(r.juzgado || r.caratula || r.expediente || r.edictoUrl || r.fechaExhibicion || r.lugarExhibicion) && (
            <div style={{ background: 'white', padding: '30px', borderRadius: '15px', marginTop: '20px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
              <h3 style={{ marginTop: 0 }}>⚖️ Causa judicial</h3>
              <div style={{ display: 'grid', gap: '10px', fontSize: '14px', color: '#444' }}>
                {r.caratula && <div><strong>Carátula:</strong> {r.caratula}</div>}
                {r.juzgado && <div><strong>Juzgado:</strong> {r.juzgado}</div>}
                {r.expediente && <div><strong>Expediente:</strong> {r.expediente}</div>}
                {r.fechaExhibicion && <div><strong>Exhibición:</strong> {fmtFecha(r.fechaExhibicion)}{r.lugarExhibicion ? ` — ${r.lugarExhibicion}` : ''}</div>}
                {!r.fechaExhibicion && r.lugarExhibicion && <div><strong>Lugar de exhibición:</strong> {r.lugarExhibicion}</div>}
              </div>
              {urlSeguraONull(r.edictoUrl) && (
                <a href={urlSeguraONull(r.edictoUrl)} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '16px', padding: '10px 18px', background: '#1a1a1a', color: 'white', borderRadius: '8px', textDecoration: 'none', fontSize: '14px', fontWeight: 'bold' }}>
                  📄 Ver edicto judicial
                </a>
              )}
            </div>
          )}
        </div>

        {/* DERECHA: datos del acto + económico + martillero */}
        <div style={{ minWidth: 0 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <span style={{ background: '#eef2ff', color: '#4f46e5', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' }}>{r.tipoBien}</span>
              <span style={{ background: '#f3f4f6', color: ESTADO_COLOR[r.estado] || '#555', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' }}>{ESTADO_LABEL[r.estado] || r.estado}</span>
            </div>
            <h1 style={{ margin: '0 0 16px', fontSize: '22px', color: '#1a1a1a' }}>{r.caratula || r.descripcion?.slice(0, 60) || 'Bien en remate'}</h1>

            {/* Fecha destacada */}
            <div style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px 18px', marginBottom: '16px' }}>
              <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#1e40af', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fecha del remate</p>
              <p style={{ margin: '0 0 10px', fontSize: '17px', color: '#1a1a1a', fontWeight: '700', textTransform: 'capitalize' }}>📅 {fmtFechaHora(r.fechaHora)} hs</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#555' }}>{MODALIDAD_LABEL[r.modalidad] || r.modalidad}</p>
              {r.modalidad !== 'online' && r.lugarRemate && <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#555' }}>📍 {r.lugarRemate}</p>}
              {r.modalidad !== 'presencial' && urlSeguraONull(r.plataformaUrl) && (
                <a href={urlSeguraONull(r.plataformaUrl)} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: '6px', fontSize: '14px', color: '#0369a1', fontWeight: '600' }}>💻 Ir a la plataforma online →</a>
              )}
              <button onClick={agendar} style={{ display: 'block', width: '100%', marginTop: '14px', padding: '12px', background: '#1e3a6e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>
                📆 Agendar (.ics)
              </button>
            </div>

            {/* Económico */}
            {(r.base != null || r.sena != null || r.comision != null || r.condiciones) && (
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ borderBottom: '2px solid #f0f2f5', paddingBottom: '10px', marginTop: 0 }}>Condiciones económicas</h3>
                {r.base != null && (
                  <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#1a1a1a', margin: '0 0 8px' }}>
                    {moneda} {Number(r.base).toLocaleString('es-AR')} <span style={{ fontSize: '14px', color: '#888', fontWeight: 'normal' }}>(base)</span>
                  </p>
                )}
                <div style={{ display: 'grid', gap: '6px', fontSize: '14px', color: '#444' }}>
                  {r.sena != null && <div>💵 <strong>Seña:</strong> {r.sena}%</div>}
                  {r.comision != null && <div>🤝 <strong>Comisión:</strong> {r.comision}%</div>}
                </div>
                {r.condiciones && <p style={{ marginTop: '12px', fontSize: '14px', color: '#555', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{r.condiciones}</p>}
              </div>
            )}

            {/* Martillero */}
            {vendedor && (
              <Link to={vendedor.slug ? `/${vendedor.slug}` : `/corredor/${vendedor.id}`} style={{ display: 'block', textDecoration: 'none', color: 'inherit', marginTop: '20px' }}>
                <div style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #eee', cursor: 'pointer' }}>
                  <p style={{ fontSize: '11px', color: '#007bff', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '10px' }}>Martillero a cargo</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {vendedor.fotoPerfilUrl
                      ? <img src={vendedor.fotoPerfilUrl} style={{ width: '54px', height: '54px', objectFit: 'cover', borderRadius: '50%', flexShrink: 0, border: '2px solid #eee' }} alt="" />
                      : vendedor.logoUrl
                        ? <img src={vendedor.logoUrl} style={{ width: '54px', height: '54px', objectFit: 'contain', flexShrink: 0 }} alt="" />
                        : <div style={{ width: '54px', height: '54px', borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>👤</div>
                    }
                    <div>
                      <p style={{ margin: 0, fontWeight: 'bold', color: '#1a1a1a', fontSize: '16px' }}>{vendedor.nombre}</p>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#007bff', fontWeight: '600' }}>Ver perfil del corredor →</p>
                    </div>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mapas: combinado (2 íconos) si el bien y el remate están cerca; separados si están lejos */}
      {(() => {
        const tieneBien = !!(r.lat && r.lng);
        const tieneRemate = !!(r.latRemate && r.lngRemate);
        if (!tieneBien && !tieneRemate) return null;

        const puntoBien = tieneBien ? [r.lat, r.lng] : null;
        const puntoRemate = tieneRemate ? [r.latRemate, r.lngRemate] : null;
        const ambosCerca = tieneBien && tieneRemate && distanciaKm(puntoBien, puntoRemate) <= UMBRAL_MAPA_UNICO_KM;

        const cardStyle = { marginTop: '30px', background: 'white', padding: '24px', borderRadius: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' };
        const mapWrap = { height: '340px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb' };
        const tile = <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />;

        if (ambosCerca) {
          return (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1a1a1a' }}>📍 Ubicaciones</h3>
              <div style={mapWrap}>
                <MapContainer center={puntoBien} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                  {tile}
                  <Marker position={puntoBien} icon={ICONO_BIEN}>
                    <Popup>🏠 <strong>Bien</strong>{r.direccion ? <><br />{r.direccion}</> : null}</Popup>
                  </Marker>
                  <Marker position={puntoRemate} icon={ICONO_REMATE}>
                    <Popup>🔨 <strong>Lugar del remate</strong>{r.lugarRemate ? <><br />{r.lugarRemate}</> : null}</Popup>
                  </Marker>
                  <AjustarEncuadre puntos={[puntoBien, puntoRemate]} />
                </MapContainer>
              </div>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '12px', fontSize: '14px', color: '#555' }}>
                <span>🏠 Ubicación del bien</span>
                <span>🔨 Lugar del remate</span>
              </div>
            </div>
          );
        }

        return (
          <>
            {tieneBien && (
              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1a1a1a' }}>📍 Ubicación del bien</h3>
                <div style={mapWrap}>
                  <MapContainer center={puntoBien} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                    {tile}
                    <Marker position={puntoBien} icon={ICONO_BIEN} />
                  </MapContainer>
                </div>
              </div>
            )}
            {tieneRemate && (
              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1a1a1a' }}>📍 Lugar del remate</h3>
                {r.lugarRemate && <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#555' }}>{r.lugarRemate}</p>}
                <div style={mapWrap}>
                  <MapContainer center={puntoRemate} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                    {tile}
                    <Marker position={puntoRemate} icon={ICONO_REMATE} />
                  </MapContainer>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Disclaimer legal */}
      <div style={{ marginTop: '30px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '18px 22px' }}>
        <p style={{ margin: 0, fontSize: '13px', color: '#92400e', lineHeight: '1.7' }}>
          ⚖️ {config.remateDisclaimer}
        </p>
      </div>
    </div>
  );
}
